import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DiscussionThreadDialog, StartDiscussionDialog } from '@/components/DiscussionDialog';
import { useAuth } from '@/contexts/AuthContext';
import { MessageSquare, Loader2, Clock, CheckCircle2, ExternalLink, ArrowRight, AlertTriangle, Bell, Calendar, Send, Search, CheckSquare, Square, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { useTerms, getTerm } from '@/lib/branding';
import UserAvatar from '@/components/UserAvatar';

export default function DiscussionsPage({ threadsOnly = false }) {
  useTerms();
  const navigate = useNavigate();
  const { user } = useAuth();
  const myId = user?.user_id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [discussions, setDiscussions] = useState([]);
  const [alertRuns, setAlertRuns] = useState([]);
  const [scheduleRuns, setScheduleRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(threadsOnly ? 'thread' : 'all');
  const [fStatus, setFStatus] = useState('all');
  const [fType, setFType] = useState('all');
  const [fPriority, setFPriority] = useState('all');
  const [fMine, setFMine] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('activity');
  const [users, setUsers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const reloadDiscussions = () => api.getMyDiscussions(showAll && isAdmin).then(d => setDiscussions(d || [])).catch(() => {});

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const exitSelect = () => { setSelectMode(false); setSelected([]); };

  const runBulk = async (action) => {
    if (selected.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.bulkDiscussionAction(selected, action);
      toast.success(`${res.updated || 0} thread${res.updated === 1 ? '' : 's'} ${action === 'resolve' ? 'resolved' : 'archived'}`);
      await reloadDiscussions();
      exitSelect();
    } catch (err) { toast.error(err.message); }
    finally { setBulkBusy(false); }
  };
  // Thread viewer — opened directly here so a started thread actually opens.
  const [threadId, setThreadId] = useState(null);
  const [threadOpen, setThreadOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const openThread = (id) => { setThreadId(id); setThreadOpen(true); };

  // Open the thread named in ?id=<discussion_id> (how every "Start thread"
  // flow lands here after creating a discussion).
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) openThread(id);
  }, [searchParams]);

  useEffect(() => {
    Promise.all([
      api.getAlerts().catch(() => []),
      api.getSchedules().catch(() => []),
      api.getMentionableUsers().catch(() => []),
      api.getMyPermissions().catch(() => ({ permissions: [] })),
    ]).then(([a, s, u, p]) => {
      setUsers(u || []);
      setIsAdmin((p?.permissions || []).includes('users:read'));
      // Build alert activity from alerts with last_triggered
      setAlertRuns((a || []).filter(al => al.last_triggered || al.enabled).map(al => ({
        type: 'alert', id: al.alert_id, name: al.name, metric: al.metric, threshold: al.threshold,
        operator: al.operator, severity: al.severity, triggered_at: al.last_triggered,
        status: al.last_triggered ? 'triggered' : 'monitoring', enabled: al.enabled,
      })));
      // Build schedule activity from schedules with last_run
      setScheduleRuns((s || []).filter(sc => sc.last_run || sc.enabled).map(sc => ({
        type: 'schedule', id: sc.schedule_id, name: sc.name, frequency: sc.frequency,
        last_run: sc.last_run, format: sc.format, recipients: sc.recipients,
        status: sc.last_run ? 'completed' : 'pending', enabled: sc.enabled,
      })));
    }).finally(() => setLoading(false));
  }, []);

  // Reload threads whenever the admin "show all" toggle changes.
  useEffect(() => {
    api.getMyDiscussions(showAll && isAdmin).then(d => setDiscussions(d || [])).catch(() => {});
  }, [showAll, isAdmin]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  // Build unified activity feed
  const allActivity = [
    ...discussions.map(d => ({ type: 'thread', id: d.discussion_id, title: d.question, groupName: d.title, subtitle: d.report_name, user: d.created_by_name, date: d.created_at, status: d.status, replies: d.replies?.length || 0, dtype: d.type, priority: d.priority, tags: d.tags || [], owner: d.owner, assigned_to: d.assigned_to || [], unread: d.unread, unread_replies: d.unread_replies || 0, last_activity_at: d.last_activity_at, raw: d })),
    ...alertRuns.map(a => ({ type: 'alert', id: a.id, title: a.name, subtitle: `${a.metric} ${a.operator === 'gt' ? '>' : '<'} ${a.threshold}`, date: a.triggered_at, status: a.status, severity: a.severity, raw: a })),
    ...scheduleRuns.map(s => ({ type: 'schedule', id: s.id, title: s.name, subtitle: `${s.frequency} • ${s.format || 'csv'}`, date: s.last_run, status: s.status, raw: s })),
  ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const nameOf = (uid) => {
    const u = users.find(x => x.user_id === uid);
    return u ? (u.name || u.email || uid) : uid;
  };

  const q = search.trim().toLowerCase();
  const matchesSearch = (a) => {
    if (!q) return true;
    if (a.type !== 'thread') return false;
    const people = [a.user, nameOf(a.owner), ...(a.assigned_to || []).map(nameOf)].filter(Boolean).join(' ');
    const hay = [a.title, a.subtitle, (a.tags || []).join(' '), people].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  };

  const filtered = (activeTab === 'all' ? allActivity : allActivity.filter(a => a.type === activeTab))
    .filter(matchesSearch)
    .filter(a => {
      if (a.type !== 'thread') return true;
      if (fStatus !== 'all' && (a.status || 'open') !== fStatus) return false;
      if (fType !== 'all' && a.dtype !== fType) return false;
      if (fPriority !== 'all' && a.priority !== fPriority) return false;
      if (fMine && !(a.assigned_to || []).includes(myId)) return false;
      return true;
    });

  const PRI_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };
  const activityOf = (a) => a.last_activity_at || a.date;
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'priority') {
      const d = (PRI_ORDER[a.priority] ?? 9) - (PRI_ORDER[b.priority] ?? 9);
      return d !== 0 ? d : new Date(activityOf(b)) - new Date(activityOf(a));
    }
    if (sortBy === 'unread') {
      const d = (b.unread ? 1 : 0) - (a.unread ? 1 : 0);
      return d !== 0 ? d : new Date(activityOf(b)) - new Date(activityOf(a));
    }
    return new Date(activityOf(b)) - new Date(activityOf(a));
  });

  const typeIcon = (type) => {
    if (type === 'thread') return <MessageSquare className="w-4 h-4" />;
    if (type === 'alert') return <AlertTriangle className="w-4 h-4" />;
    if (type === 'schedule') return <Send className="w-4 h-4" />;
    return <Bell className="w-4 h-4" />;
  };

  const typeColor = (type) => {
    if (type === 'thread') return 'bg-primary/10 border-primary/20 text-primary';
    if (type === 'alert') return 'bg-amber-500/10 border-amber-500/20 text-amber-400';
    if (type === 'schedule') return 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400';
    return 'bg-muted/10 border-border/20 text-muted-foreground';
  };

  return (
    <div className="p-6 space-y-6" data-testid={threadsOnly ? 'discussions-page' : 'activity-page'}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">{threadsOnly ? getTerm('discussions') : 'Activity'}</h1>
          <p className="text-sm text-muted-foreground mt-1">{threadsOnly ? 'Threads you started or were assigned to' : 'Threads, alert triggers, and scheduled deliveries'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setNewOpen(true)} className="h-9 px-3 rounded-lg text-xs font-semibold bg-primary text-primary-foreground flex items-center gap-1.5 hover:bg-primary/90 active:scale-95 transition-all" data-testid="new-discussion-btn">
            <MessageSquare className="w-3.5 h-3.5" /> New {getTerm('discussions', 'singular')}
          </button>
          {!threadsOnly && (
            <div className="flex items-center gap-1 rounded-lg border border-border/50 overflow-hidden">
              {[['all', 'All', allActivity.length], ['thread', 'Threads', discussions.length], ['alert', 'Alerts', alertRuns.length], ['schedule', 'Schedules', scheduleRuns.length]].map(([key, label, count]) => (
                <button key={key} onClick={() => setActiveTab(key)} className={`px-3 py-1.5 text-xs transition-colors ${activeTab === key ? 'bg-primary/20 text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`activity-tab-${key}`}>
                  {label} ({count})
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {(threadsOnly || activeTab === 'thread' || activeTab === 'all') && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="discussion-filters">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search keyword, tag, or person" className="h-8 w-56 rounded-lg bg-muted/30 border border-border/40 pl-7 pr-2 text-xs text-foreground placeholder:text-muted-foreground/80" data-testid="discussion-search" />
          </div>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)} className="h-8 rounded-lg bg-muted/30 border border-border/40 px-2 text-xs text-foreground" data-testid="filter-status">
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
          <select value={fType} onChange={e => setFType(e.target.value)} className="h-8 rounded-lg bg-muted/30 border border-border/40 px-2 text-xs text-foreground" data-testid="filter-type">
            <option value="all">All types</option>
            <option value="question">Question</option>
            <option value="data_issue">Data Issue</option>
            <option value="action_item">Action Item</option>
            <option value="feature_request">Feature Request</option>
            <option value="other">Other</option>
          </select>
          <select value={fPriority} onChange={e => setFPriority(e.target.value)} className="h-8 rounded-lg bg-muted/30 border border-border/40 px-2 text-xs text-foreground" data-testid="filter-priority">
            <option value="all">All priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
          <button onClick={() => setFMine(v => !v)} className={`h-8 px-3 rounded-lg text-xs border transition-colors ${fMine ? 'bg-primary/20 text-primary border-primary/40' : 'bg-muted/30 text-muted-foreground border-border/40 hover:text-foreground'}`} data-testid="filter-mine">
            Assigned to me
          </button>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="h-8 rounded-lg bg-muted/30 border border-border/40 px-2 text-xs text-foreground" data-testid="filter-sort">
            <option value="activity">Newest activity</option>
            <option value="priority">Priority</option>
            <option value="unread">Unread first</option>
          </select>
          {isAdmin && (
            <label className="h-8 px-3 rounded-lg text-xs border border-border/40 bg-muted/30 flex items-center gap-2 cursor-pointer text-muted-foreground hover:text-foreground" data-testid="filter-show-all">
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} className="accent-primary w-3.5 h-3.5" data-testid="filter-show-all-checkbox" />
              Show all (admin)
            </label>
          )}
          {isAdmin && !selectMode && (
            <button onClick={() => setSelectMode(true)} className="h-8 px-3 rounded-lg text-xs border border-border/40 bg-muted/30 text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="bulk-select-btn">
              <CheckSquare className="w-3.5 h-3.5" /> Select
            </button>
          )}
        </div>
      )}

      {selectMode && (
        <div className="flex items-center gap-2 flex-wrap rounded-xl border border-primary/30 bg-primary/5 px-3 py-2" data-testid="bulk-action-bar">
          <span className="text-xs font-medium text-foreground" data-testid="bulk-selected-count">{selected.length} selected</span>
          <div className="flex-1" />
          <button onClick={() => runBulk('resolve')} disabled={selected.length === 0 || bulkBusy} className="h-8 px-3 rounded-lg text-xs bg-emerald-600 text-white disabled:opacity-40 flex items-center gap-1.5" data-testid="bulk-resolve-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
          </button>
          <button onClick={() => runBulk('archive')} disabled={selected.length === 0 || bulkBusy} className="h-8 px-3 rounded-lg text-xs bg-muted text-foreground border border-border/40 disabled:opacity-40 flex items-center gap-1.5" data-testid="bulk-archive-btn">
            <Archive className="w-3.5 h-3.5" /> Archive
          </button>
          <button onClick={exitSelect} className="h-8 px-3 rounded-lg text-xs text-muted-foreground hover:text-foreground" data-testid="bulk-cancel-btn">Cancel</button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="discussions-empty">
          <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10 mb-4"><MessageSquare className="w-8 h-8 text-primary/40" /></div>
          <h3 className="text-base font-heading font-semibold text-foreground mb-1">{threadsOnly ? `No ${getTerm('discussions', 'plural', 'lower')} yet` : 'No activity'}</h3>
          <p className="text-sm text-muted-foreground max-w-sm">{threadsOnly ? `Start a thread from any ${getTerm('ai_explore')} answer, ${getTerm('insights', 'singular', 'lower')}, ${getTerm('investigations', 'singular', 'lower')}, or report and it will appear here.` : 'Threads, alert triggers, and scheduled report deliveries will appear here.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map(item => (
            <div key={`${item.type}-${item.id}`}
              onClick={() => {
                if (selectMode) {
                  if (item.type === 'thread') toggleSelect(item.id);
                  return;
                }
                if (item.type === 'thread') {
                  openThread(item.raw.discussion_id);
                } else if (item.type === 'alert') navigate('/alerts');
                else if (item.type === 'schedule') navigate('/scheduling');
              }}
              className={`glass-card rounded-xl p-4 transition-all group ${selectMode && item.type !== 'thread' ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:border-primary/20'} ${selectMode && selected.includes(item.id) ? 'border-primary/50 ring-1 ring-primary/30' : ''}`}
              data-testid={`activity-${item.type}-${item.id}`}>
              <div className="flex items-start gap-3">
                {selectMode && item.type === 'thread' && (
                  <div className="pt-0.5 flex-shrink-0" data-testid={`bulk-checkbox-${item.id}`}>
                    {selected.includes(item.id)
                      ? <CheckSquare className="w-4 h-4 text-primary" />
                      : <Square className="w-4 h-4 text-muted-foreground/70" />}
                  </div>
                )}
                <div className={`p-2 rounded-lg border ${typeColor(item.type)} flex-shrink-0`}>
                  {typeIcon(item.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge variant="outline" className="text-[7px] uppercase tracking-wider">{item.type}</Badge>
                    {item.date && <span className="text-[10px] text-muted-foreground">{fmtDate(item.date)}</span>}
                    {item.type === 'thread' && (
                      item.status === 'resolved'
                        ? <Badge className="text-[7px] bg-emerald-600 text-white"><CheckCircle2 className="w-2 h-2 mr-0.5" />Resolved</Badge>
                        : <Badge className="text-[7px] bg-amber-500 text-white">Open</Badge>
                    )}
                    {item.type === 'alert' && item.severity && <Badge className={`text-[7px] ${item.severity === 'critical' ? 'bg-red-500/20 text-red-400' : item.severity === 'high' ? 'bg-orange-500/20 text-orange-400' : 'bg-amber-500/20 text-amber-400'}`}>{item.severity}</Badge>}
                    {item.type === 'alert' && <Badge variant="outline" className={`text-[7px] ${item.status === 'triggered' ? 'border-red-500/30 text-red-400' : 'border-emerald-500/30 text-emerald-400'}`}>{item.status}</Badge>}
                    {item.type === 'schedule' && <Badge variant="outline" className={`text-[7px] ${item.status === 'completed' ? 'border-emerald-500/30 text-emerald-400' : 'border-muted-foreground/30 text-muted-foreground'}`}>{item.status}</Badge>}
                    {item.replies > 0 && <Badge variant="outline" className="text-[7px]">{item.replies} replies</Badge>}
                    {item.type === 'thread' && item.unread_replies > 0
                      ? <Badge className="text-[7px] bg-blue-500 text-white" data-testid={`thread-unread-${item.id}`}>{item.unread_replies} new {item.unread_replies > 1 ? 'replies' : 'reply'}</Badge>
                      : item.type === 'thread' && item.unread && <Badge className="text-[7px] bg-blue-500 text-white" data-testid={`thread-unread-${item.id}`}>New</Badge>}
                  </div>
                  <p className="text-sm font-medium text-foreground">{item.groupName || item.title}</p>
                  {item.groupName && <p className="text-[11px] text-foreground/70 mt-0.5 line-clamp-1">{item.title}</p>}
                  <p className="text-[11px] text-muted-foreground mt-0.5">{item.subtitle}</p>
                  {item.user && <p className="text-[10px] text-muted-foreground/80 mt-1">by {item.user}</p>}
                  {item.type === 'thread' && (item.assigned_to || []).length > 0 && (
                    <div className="flex items-center mt-1.5" data-testid={`participant-avatars-${item.id}`}>
                      {(item.assigned_to || []).slice(0, 5).map((uid, i) => {
                        const u = users.find(x => x.user_id === uid);
                        return (
                          <div key={uid} className="-ml-1.5 first:ml-0" style={{ zIndex: 5 - i }} title={u?.name || u?.email || uid}>
                            <UserAvatar name={u?.name || u?.email || uid} src={u?.avatarUrl || ''} size={22} ring="ring-2 ring-background" />
                          </div>
                        );
                      })}
                      {(item.assigned_to || []).length > 5 && (
                        <div className="-ml-1.5 w-[22px] h-[22px] rounded-full bg-muted border-2 border-background flex items-center justify-center text-[8px] font-semibold text-muted-foreground">+{(item.assigned_to || []).length - 5}</div>
                      )}
                    </div>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground/20 group-hover:text-primary transition-colors flex-shrink-0 mt-2" />
              </div>
            </div>
          ))}
        </div>
      )}

      <DiscussionThreadDialog
        open={threadOpen}
        onOpenChange={(v) => {
          setThreadOpen(v);
          if (!v) {
            // Refresh the list (reply counts / new thread) and drop ?id=.
            api.getMyDiscussions(showAll && isAdmin).then((d) => setDiscussions(d || [])).catch(() => {});
            if (searchParams.get('id')) {
              const next = new URLSearchParams(searchParams);
              next.delete('id');
              setSearchParams(next, { replace: true });
            }
          }
        }}
        discussionId={threadId}
      />

      <StartDiscussionDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(disc) => {
          reloadDiscussions();
          if (disc?.discussion_id) openThread(disc.discussion_id);
        }}
      />
    </div>
  );
}
